import { useEffect, useRef } from 'react';
import type { HostBridge } from '../../../host.js';
import type { Attachment, CaptureBusyState } from '../types.js';

type CaptureKind = 'screenshot' | 'recording';
type Waiter = (attachment: Attachment | null) => void;

interface CaptureBridge {
  requestCapture: (kind: CaptureKind) => Promise<Attachment | null>;
}

interface UseCaptureBridgeOptions {
  bridge: HostBridge;
  onBusy: (state: CaptureBusyState) => void;
  onError: (message: string) => void;
}

export function useCaptureBridge({ bridge, onBusy, onError }: UseCaptureBridgeOptions): CaptureBridge {
  const waitersRef = useRef<Map<string, Waiter>>(new Map());

  useEffect(() => {
    return bridge.subscribe((message) => {
      if (message.type !== 'chirp:capture:result') return;
      const payload = message.payload as {
        requestId: string;
        kind: CaptureKind;
        status: 'success' | 'cancelled' | 'error';
        dataUrl?: string;
        filename?: string;
        error?: string;
      };
      const waiter = waitersRef.current.get(payload.requestId);
      waitersRef.current.delete(payload.requestId);
      if (!waiter) return;
      if (payload.status === 'success' && payload.dataUrl) {
        waiter({ url: payload.dataUrl, kind: payload.kind, filename: payload.filename });
        return;
      }
      if (payload.status === 'error' && payload.error) onError(payload.error);
      waiter(null);
    });
  }, [bridge, onError]);

  const requestCapture = (kind: CaptureKind): Promise<Attachment | null> => {
    return new Promise<Attachment | null>((resolve) => {
      const requestId = `cap_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      waitersRef.current.set(requestId, resolve);
      onBusy(kind);
      bridge.send('chirp:capture:request' as never, { kind, requestId } as never);
    }).then((attachment) => {
      onBusy(null);
      return attachment;
    });
  };

  return { requestCapture };
}
